import {
  ChartNoAxesColumnIcon, ChevronLeftIcon, ChevronRightIcon, CreditCardIcon,
  FileTextIcon, Gr, LayoutDashboardIcon, PercentIcon, LogOutIcon, MenuIcon,
  PackageOpenIcon, React, ShoppingCartIcon, TagIcon, TicketIcon, UserCogIcon,
  UserPlusIcon, UsersIcon, XIcon, useLocation, useNavigate,
} from "../vendor/runtime.js";
import { useAdminAuth } from "../context/AdminAuthContext.jsx";

const STORAGE_KEY = "cb_admin_sidebar_collapsed";
const navItems = [
  { path: "/admin", icon: LayoutDashboardIcon, label: "Dashboard" },
  { path: "/admin/products", icon: PackageOpenIcon, label: "Products" },
  { path: "/admin/orders", icon: ShoppingCartIcon, label: "Orders" },
  { path: "/admin/custom", icon: FileTextIcon, label: "Requests" },
  { path: "/admin/support", icon: TicketIcon, label: "Support" },
  { path: "/admin/certificates", icon: TagIcon, label: "Certificates" },
  { path: "/admin/coupons", icon: PercentIcon, label: "Coupons" },
  { path: "/admin/price-management", icon: TagIcon, label: "Price Management" },
  { path: "/admin/users", icon: UserCogIcon, label: "Users" },
  { path: "/admin/agents", icon: UsersIcon, label: "Agents" },
  { path: "/admin/withdrawals", icon: CreditCardIcon, label: "Withdrawals" },
  { path: "/admin/analytics", icon: ChartNoAxesColumnIcon, label: "Analytics" },
];

export const AdminNavbar = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const drawerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const { adminUser, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const name = adminUser?.name || adminUser?.email || "Admin";

  React.useLayoutEffect(() => {
    document.documentElement.style.setProperty("--admin-sidebar-width", collapsed ? "68px" : "260px");
    try { localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0"); } catch { /* storage is optional */ }
  }, [collapsed]);
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  React.useEffect(() => {
    const media = window.matchMedia("(min-width: 900px)");
    const closeOnDesktop = () => { if (media.matches) setMobileOpen(false); };
    media.addEventListener("change", closeOnDesktop);
    return () => media.removeEventListener("change", closeOnDesktop);
  }, []);
  React.useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    drawerRef.current?.querySelector("button")?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") { event.preventDefault(); setMobileOpen(false); }
      if (event.key === "Tab") {
        const items = [...drawerRef.current.querySelectorAll("a[href],button:not([disabled])")]
          .filter((element) => element.getClientRects().length);
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
      menuRef.current?.focus();
    };
  }, [mobileOpen]);
  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  const active = (path) => path === "/admin" ? location.pathname === path :
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const content = (mini = false, mobile = false) => (
    <div className={`admin-sidebar-content${mini ? " is-collapsed" : ""}`}>
      <div className="admin-brand">
        <img src="/Logo.png" alt="CodeBruh" />
        {!mini && <div className="admin-brand-copy"><strong>CodeBruh Admin</strong><span>Management System</span></div>}
        {mobile && <button className="admin-icon-button" onClick={() => setMobileOpen(false)} aria-label="Close menu"><XIcon size={20} /></button>}
      </div>
      {!mobile && <div className="admin-collapse-row">
        <button className="admin-icon-button" onClick={() => setCollapsed(!collapsed)}
          aria-label={mini ? "Expand sidebar" : "Collapse sidebar"} title={mini ? "Expand sidebar" : "Collapse sidebar"} aria-expanded={!mini}>
          {mini ? <ChevronRightIcon size={18} /> : <ChevronLeftIcon size={18} />}
        </button>
      </div>}
      <nav className="admin-nav" aria-label={mobile ? "Mobile administration" : "Administration"}>
        {navItems.map(({ path, label, icon: Icon }) => (
          <Gr key={path} to={path} onClick={() => setMobileOpen(false)}
            className={`admin-nav-link${active(path) ? " is-active" : ""}`}
            aria-current={active(path) ? "page" : undefined} aria-label={mini ? label : undefined} title={mini ? label : undefined}>
            <Icon size={21} aria-hidden="true" />{!mini && <span>{label}</span>}
          </Gr>
        ))}
      </nav>
      <div className="admin-sidebar-footer">
        <Gr to="/admin/register-agent" className={`admin-nav-link${active("/admin/register-agent") ? " is-active" : ""}`}
          aria-label={mini ? "Register Agent" : undefined} title={mini ? "Register Agent" : undefined}
          aria-current={active("/admin/register-agent") ? "page" : undefined}>
          <UserPlusIcon size={21} aria-hidden="true" />{!mini && <span>Register Agent</span>}
        </Gr>
        <div className="admin-profile">
          <span className="admin-avatar" title={name}>{name.charAt(0).toUpperCase()}</span>
          {!mini && <div className="admin-profile-copy"><strong>{name}</strong><span title={adminUser?.email}>{adminUser?.email || "Administrator"}</span></div>}
          <button className="admin-icon-button" onClick={handleLogout} aria-label="Sign out" title="Sign out"><LogOutIcon size={17} /></button>
        </div>
      </div>
    </div>
  );
  return <>
    <header className="admin-mobile-bar">
      <button ref={menuRef} className="admin-icon-button" onClick={() => setMobileOpen(true)} aria-label="Open menu" aria-expanded={mobileOpen} aria-controls="admin-mobile-drawer"><MenuIcon size={22} /></button>
      <img src="/Logo.png" alt="" /><strong>CodeBruh Admin</strong><span className="admin-avatar">{name.charAt(0).toUpperCase()}</span>
    </header>
    <aside className="admin-desktop-sidebar" aria-label="Admin sidebar">{content(collapsed)}</aside>
    {mobileOpen && <div className="admin-mobile-overlay">
      <div className="admin-drawer-backdrop" onClick={() => setMobileOpen(false)} />
      <aside id="admin-mobile-drawer" ref={drawerRef} className="admin-mobile-drawer" role="dialog" aria-modal="true" aria-label="Navigation menu">{content(false, true)}</aside>
    </div>}
  </>;
};
